/**
 * Aventross AGM (Audited) — 화면 값(fill) → 템플릿 [[변수]] / 반복 배열.
 *   · 화면 목록(useRepeat): mem_{i}/ · dir_{i}/(+ dir_{i}/int_{j}/) · ctrl_{i}/(Annex A) · nctrl_{i}/(Notice) · nd_{i}/ · ns_{i}/
 *   · 반복 항목에는 회사 공통 값(최상위)을 같이 넣는다 — 백엔드는 반복 안에서 항목에 없는 [[변수]]를 비운다
 *     (Disclosure 의 회사명·AGM 날짜, Annex 의 "As of" 날짜 등).
 *   · directors[].other_interests = 이사별 이해관계 표 행(백엔드 겹친 반복).
 *   · Annex A/B/C 는 0명이어도 표 1벌("As of …, the Company has not received …")을 남긴다 → 빈 항목 1개.
 *   · 날짜는 ISO 그대로 — 백엔드가 세션 형식으로 바꾼다(반복 항목·안쪽 행 포함).
 */
import { repeatCount } from '../useRepeat';

const st = (v: unknown) => String(v ?? '').trim();

function itemsOf(values: Record<string, string>, base: string, common: Record<string, string>): Array<Record<string, any>> {
  const out: Array<Record<string, any>> = [];
  const n = repeatCount(values, base);
  for (let i = 1; i <= n; i += 1) {
    const pre = `${base}_${i}/`;
    const item: Record<string, any> = { ...common };
    for (const [k, v] of Object.entries(values)) {
      if (!k.startsWith(pre)) continue;
      const rest = k.slice(pre.length);
      if (rest.includes('/') || rest.endsWith('__n')) continue; // 하위 목록(int_…)은 따로
      item[rest] = v;
    }
    out.push(item);
  }
  return out;
}

export function normalizeAventrossAgmForWord(values: Record<string, string>): Record<string, any> {
  const common: Record<string, string> = {};
  for (const [k, v] of Object.entries(values)) {
    if (k.includes('/') || k.endsWith('__n')) continue;
    common[k] = v;
  }
  const out: Record<string, any> = { ...common };

  out.members = itemsOf(values, 'mem', common).filter((m) => st(m.member_name));

  out.directors = itemsOf(values, 'dir', common).map((d, i): Record<string, any> => {
    const rows = itemsOf(values, `dir_${i + 1}/int`, {});
    // 이해관계가 없으면 원본처럼 빈 행 1줄(표가 머리글만 남지 않게)
    return { ...d, other_interests: rows.length ? rows : [{ interest_corporation: '', interest_nature: '', interest_effective_date: '' }] };
  }).filter((d) => st(d.director_name));

  // 컨트롤러별 Notice — 엔티티 현재 컨트롤러 목록(nctrl). 없으면 생성 안 함.
  //   Annex A(등록부, ctrl)와 출처가 달라 목록을 따로 둔다 — Notice 는 현재 컨트롤러에게 보내는 질의서.
  out.other_controllers = itemsOf(values, 'nctrl', common)
    .filter((c) => st(c.controller_name) && !st(c.controller_cessation_date));
  const ctrls = itemsOf(values, 'ctrl', common).filter((c) => st(c.controller_name));
  out.registrable_controllers = ctrls.length ? ctrls : [{ ...common }]; // Annex A — 등록부 전체, 없으면 빈 표 1벌

  const nds = itemsOf(values, 'nd', common).filter((x) => st(x.nominee_director_name) || st(x.nominee_director_nominator_full_name));
  out.nominee_directors = nds.length ? nds : [{ ...common }];
  const nss = itemsOf(values, 'ns', common).filter((x) => st(x.nominee_shareholder_name) || st(x.nominee_shareholder_nominator_full_name));
  out.nominee_shareholders = nss.length ? nss : [{ ...common }];

  return out;
}
